import React, {useEffect} from "react";
import {
    IonButton,
    IonCard, IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCol, IonGrid, IonRow, IonSelect, IonSelectOption,
    IonContent, IonIcon, IonInput, IonLabel,
    IonLoading,
    IonPage
} from "@ionic/react";
import {
    arrowDownOutline,
    arrowDownSharp,
    arrowUpOutline,
    arrowUpSharp, personOutline, personSharp
} from "ionicons/icons";
import {AcaHeader} from "./AcaHeader";
import {useHistory} from "react-router";
import useAuth from "../../hooks/useAuth";
import {ContactTypeBrief} from "./types";
import {useMutation} from "@apollo/client";
import {ACA_SEARCH} from "../../schemas/aca";
import {formatDatetime, formatPhone, formatStatus} from "../../utils";
import PagingButtons from "../../components/PagingButtons";

const AcaAdvancedSearch: React.FC<any> = () => {
    const history = useHistory();
    const {claims} = useAuth();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);

    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [contacts, setContacts] = React.useState<ContactTypeBrief[] | undefined>(undefined);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [numPages, setNumPages] = React.useState<number>(1);
    const [orderColumn, setOrderColumn] = React.useState<string>('CREATED_DATETIME');
    const [orderDirection, setOrderDirection] = React.useState<string>('DESC');
    const [totalCount, setTotalCount] = React.useState<number | undefined>(undefined);

    const [quickSearch, setQuickSearch] = React.useState<string | null | undefined>(undefined);
    const [contactId, setContactId] = React.useState<string | null | undefined>(undefined);
    const [opportunityId, setOpportunityId] = React.useState<string | null | undefined>(undefined);
    const [firstName, setFirstName] = React.useState<string | null | undefined>(undefined);
    const [lastName, setLastName] = React.useState<string | null | undefined>(undefined);
    const [phone, setPhone] = React.useState<string | null | undefined>(undefined);
    const [email, setEmail] = React.useState<string | null | undefined>(undefined);
    const [pageSize, setPageSize] = React.useState<number>(10);

    const [acaSearchMutation] = useMutation(ACA_SEARCH);
    useEffect(() => {

        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
    }, []);
    useEffect(() => {
        // if user already searched then refresh results with new page size
        if (contacts && contacts.length >= 0) {
            setCurrentPage(1);
            void loadDataQuickSearch(1, 'CREATED_DATETIME', 'DESC');
        }
    }, [pageSize]);

    const clearFilters = () => {
        setQuickSearch('');
        setContactId('');
        setOpportunityId('');
        setFirstName('');
        setLastName('');
        setPhone('');
        setEmail('');

        // reset ordering & pagination
        setOrderColumn('CREATED_DATETIME');
        setOrderDirection('DESC');
        setCurrentPage(1);
        setPageSize(10);

        // clear results
        setContacts(undefined);
        setNumPages(1);
        setTotalCount(undefined);
    };

    const loadDataQuickSearch = async (page: number, column?: string, direction?: string) => {
        setIsLoading(true);
        if (!column) {
            column = orderColumn;
        }
        if (!direction) {
            direction = orderDirection;
        }
        try {
            let searchResults;
            if (quickSearch && quickSearch.length > 0) {
                searchResults = await acaSearchMutation({variables: {data: {quickSearch: quickSearch, pageSize: pageSize, currentPage: page, orderColumn: column, orderDirection: direction}}});
            } else {
                searchResults = await acaSearchMutation({variables: {data: {contactId: contactId ? Number(contactId) : null, opportunityId: opportunityId ? Number(opportunityId) : null, firstName: firstName, lastName: lastName, phone: phone, email: email, pageSize: pageSize, currentPage: page, orderColumn: column, orderDirection: direction}}});
            }
            if (searchResults.data?.acaSearch.success) {
                setContacts(searchResults.data.acaSearch.contacts);
                setNumPages(searchResults.data.acaSearch.pagination.numOfPages);
                setTotalCount(searchResults.data.acaSearch.pagination.totalCount);
            }
        } catch (e: any) {
            console.error(e);
            alert(e.message);
        } finally {
            setIsLoading(false);
        }
    };
    const performSearch = async () => {
        if (quickSearch && quickSearch.length > 0 ||
            (opportunityId || contactId || firstName || lastName || phone || email)) {
            await loadDataQuickSearch(1, 'CREATED_DATETIME', 'DESC');
            setPageSize(10);
        }
    };

    const changePage = async (page: number) => {
        setCurrentPage(Number(page));
        await loadDataQuickSearch(page);
    }

    const handlePageSizeChange = async (value: number) => {
        const next = Number(value) || 10;
        if (next !== pageSize) {
            setPageSize(next);
            setCurrentPage(1);
            await loadDataQuickSearch(1, 'CREATED_DATETIME', 'DESC');
        }
    };

    const toggleOrdering = async (column: string) => {
        if (column != orderColumn) {
            setOrderColumn(column);
            setOrderDirection('DESC');
            await loadDataQuickSearch(1, column, 'DESC');
        } else {
            if (orderDirection === 'DESC') {
                setOrderDirection('ASC');
                await loadDataQuickSearch(1, column, 'ASC');
            } else {
                setOrderDirection('DESC');
                await loadDataQuickSearch(1, column, 'DESC');
            }
        }
    };

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Advanced Search"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-advanced-search">
                <div style={{padding: '10px'}}>

                    <IonCard>
                        <IonCardHeader>
                            <IonCardTitle>Search Filters</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <p>Choose the fields you would like to search on below.</p>

                            <IonGrid>
                                <IonRow>
                                    <IonCol size="12">
                                        <IonInput label="Quick Search" labelPlacement="floating" fill="outline" style={{width: '50%'}}
                                            onIonInput={(e) => setQuickSearch((e.detail.value ?? '').trim())}
                                        />
                                    </IonCol>
                                </IonRow>

                                <IonRow className="ion-padding-top ion-padding-bottom">
                                    <IonCol size="12" className="ion-text-center">
                                        OR
                                    </IonCol>
                                </IonRow>



                                <IonRow>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="First Name" labelPlacement="floating" fill="outline"
                                            onIonInput={(e) => setFirstName(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="Last Name" labelPlacement="floating" fill="outline"
                                            onIonInput={(e) => setLastName(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                </IonRow>

                                <IonRow>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="Phone #" labelPlacement="floating" fill="outline" inputmode="tel"
                                            onIonInput={(e) => setPhone(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="Email" labelPlacement="floating" fill="outline" type="email"
                                            onIonInput={(e) => setEmail(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="Contact ID" labelPlacement="floating" fill="outline" inputmode="numeric"
                                                  onIonInput={(e) => setContactId(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                    <IonCol size="12" sizeMd="6">
                                        <IonInput label="Opportunity ID" labelPlacement="floating" fill="outline" inputmode="numeric"
                                                  onIonInput={(e) => setOpportunityId(e.detail.value ?? '')}
                                        />
                                    </IonCol>
                                </IonRow>

                                <IonRow>
                                    <IonCol size="12" className="ion-text-right">
                                        <IonButton fill="solid" color="light" onClick={clearFilters} style={{ marginRight: 8 }}>
                                            Clear
                                        </IonButton>
                                        <IonButton onClick={() => performSearch()}>Search</IonButton>
                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                        </IonCardContent>
                    </IonCard>

                    {contacts && contacts.length > 0 &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Search Results - ACA Contacts</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <p><b>Total Contact Count:</b> {totalCount}</p>
                                {numPages &&
                                    <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                                }
                                {numPages && numPages > 1 && (
                                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8 }}>
                                        <IonLabel style={{ fontSize: 12, minWidth: 90 }}>Results per page</IonLabel>
                                        <IonSelect
                                            value={pageSize}
                                            interface="popover"
                                            onIonChange={(e) => handlePageSizeChange(e.detail.value)}
                                            style={{ minWidth: 120 }}
                                        >
                                            <IonSelectOption value={10}>10</IonSelectOption>
                                            <IonSelectOption value={25}>25</IonSelectOption>
                                            <IonSelectOption value={50}>50</IonSelectOption>
                                            <IonSelectOption value={100}>100</IonSelectOption>
                                        </IonSelect>
                                    </div>
                                )}

                                <table className="aca-healthcare" style={{width: '100%'}}>
                                    <thead>
                                    <tr>
                                        <th>Contact ID</th>
                                        <th onClick={() => toggleOrdering('FIRST_NAME')} style={{cursor: 'pointer'}}>First
                                            Name {orderColumn === 'FIRST_NAME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'FIRST_NAME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('LAST_NAME')} style={{cursor: 'pointer'}}>Last
                                            Name {orderColumn === 'LAST_NAME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'LAST_NAME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('STATUS')} style={{cursor: 'pointer'}}>Status {orderColumn === 'STATUS' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'STATUS' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('PHONE')} style={{cursor: 'pointer'}}>Phone {orderColumn === 'PHONE' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'PHONE' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('EMAIL')} style={{cursor: 'pointer'}}>Email {orderColumn === 'EMAIL' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'EMAIL' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th>Address</th>
                                        <th onClick={() => toggleOrdering('CREATED_DATETIME')} style={{cursor: 'pointer'}}>Created
                                            Datetime {orderColumn === 'CREATED_DATETIME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'CREATED_DATETIME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('LAST_UPDATED_DATETIME')} style={{cursor: 'pointer'}}>Last
                                            Updated
                                            Datetime {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {contacts && contacts.map((contact, index) => {
                                        return (
                                            <tr key={'contact_' + index}>
                                                <td style={{textAlign: 'center'}}>{contact.contactId}</td>
                                                <td>{contact.firstName}</td>
                                                <td>{contact.lastName}</td>
                                                <td>{formatStatus(contact.status)}</td>
                                                <td>{formatPhone(contact.phone)}</td>
                                                <td>{contact.email}</td>
                                                <td>{contact.addressLine1}<br/>
                                                    {contact.addressLine2 && <>{contact.addressLine2}<br/></>}
                                                    {contact.addressCity}, {contact.addressState} {contact.addressZip}</td>
                                                <td>{formatDatetime(contact.createdDatetime)}</td>
                                                <td>{formatDatetime(contact.lastUpdatedDatetime)}</td>
                                                <td>
                                                    <a href={'/aca/view-contact/' + contact.contactId}><IonIcon md={personOutline} ios={personSharp}></IonIcon></a>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                {numPages &&
                                    <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                                }
                                <div style={{height: '60px'}}></div>

                            </IonCardContent>
                        </IonCard>
                    }

                </div>
            </IonContent>
        </IonPage>
    );
}

export default AcaAdvancedSearch;